/**
 * The AI Team page: everything the app's AI found on its own, as the work of
 * three named employees (ADR 0259, issue #682).
 *
 *   Iris   Inbox      a customer reply read as "said go ahead" or "says they
 *                     paid", until the job is sold or a payment lands
 *   Paige  Paperwork  payment terms the contract doesn't show (ADR 0226)
 *   Max    Materials  an order that doesn't show what was asked for (ADR 0234)
 *
 * This turns each source's rows into one list of findings, open or dismissed,
 * and counts how often each employee was marked wrong. Anyone dismisses a
 * finding as wrong, or as right but fine; the reason is what makes the count.
 */

import {
  MATERIALS_WARN_AT,
  materialsDismissed,
  materialsQuote,
  materialsWarning,
} from "./materialsCheck.js";
import type { MaterialsCheckRow } from "./materialsCheck.js";
import {
  paymentTermsWarning,
  TERMS_WARN_AT,
  termsDismissed,
} from "./paymentTermsCheck.js";
import type { PaymentTermsCheckRow } from "./paymentTermsCheck.js";
import { REPLY_LIKELY_FROM, replyCertainty } from "./replyReading.js";
import { shortDayMonth } from "./dates.js";

// Sully (supplier matches) is off the page while the check behind it is off:
// Jonathon, 2026-10-03, "leave it off" (ADR 0259).
export type AiEmployee = "iris" | "paige" | "max";
export type AiSource = "reading" | "terms" | "materials";
export type AiReason = "wrong" | "fine";

export const AI_EMPLOYEES: Record<
  AiEmployee,
  { name: string; role: string; does: string }
> = {
  iris: {
    name: "Iris",
    role: "Inbox",
    does: "Reads customer replies for a go-ahead or a payment.",
  },
  paige: {
    name: "Paige",
    role: "Paperwork",
    does: "Checks what customers were promised about paying against the contract.",
  },
  max: {
    name: "Max",
    role: "Materials",
    does: "Checks material orders against what the customer asked for.",
  },
};

export const AI_EMPLOYEE_ORDER: readonly AiEmployee[] = [
  "iris",
  "paige",
  "max",
];

/** Dismissed findings are kept on the page this long. */
export const AI_DISMISSED_DAYS = 90;
/** "This month" for an employee's count. */
export const AI_MONTH_DAYS = 30;
/** How far back a reply can still be a finding: the to-do list's window. */
export const AI_READING_DAYS = 14;

/** The job card that holds a finding's evidence. */
export type AiCard = "Emails" | "Texts" | "Notes";

const CARD_OF_KIND: Record<string, AiCard> = {
  email: "Emails",
  text: "Texts",
  note: "Notes",
  call: "Notes",
};

/** The card a quoted message lives on. */
const cardOf = (kind: string | null | undefined): AiCard | null =>
  (kind && CARD_OF_KIND[kind]) || null;

export interface AiFinding {
  /** `source:id`, unique on the page. */
  key: string;
  source: AiSource;
  /** What the dismiss function is given: a job id or a reading id. */
  sourceId: string;
  employee: AiEmployee;
  jobId: string | null;
  title: string;
  /**
   * The finding as its employee would say it, for the feed: "Dale Whitfield
   * was promised a way of paying that the contract doesn’t show."
   */
  voice: string;
  /** What was said: the message, or our order line. */
  said: { label: string; text: string } | null;
  /** What the record shows instead. */
  record: { label: string; text: string } | null;
  /**
   * The difference in one line, when the check wrote one: "Asked for Hardie
   * fiber cement siding and trim; the order has only apron flashing."
   */
  summary: string | null;
  probability: number;
  /** Under 90%: the page says "Looks like". */
  likely: boolean;
  /** When the thing it is about happened: the message. */
  raisedAt: string;
  /** The job card that holds the evidence, for the link. */
  card: AiCard | null;
  status: "open" | "dismissed";
  /**
   * Whether Reopen is offered. Not for a reply marked handled on Messages
   * with no reason: that was not this page's dismissal to take back.
   */
  reopenable: boolean;
  /** Null for a dismissal that gave no reason (the job page, Messages). */
  reason: AiReason | null;
  dismissedBy: string | null;
  dismissedAt: string | null;
}

export type AiTermsRow = PaymentTermsCheckRow & {
  checked_at: string;
  dismissed_reason?: string | null;
  dismissed_by?: string | null;
  dismissed_at?: string | null;
};

export type AiMaterialsRow = MaterialsCheckRow & {
  checked_at: string;
  dismissed_reason?: string | null;
  dismissed_by?: string | null;
  dismissed_at?: string | null;
};

export interface AiReadingRow {
  id: string;
  meaning: string;
  probability: number | string;
  sender_kind: string | null;
  job_id: string | null;
  sent_at: string;
  kind: "email" | "text";
  /**
   * An email's Message-ID. One customer email seen in two mailboxes is two
   * rows with one of these, and is one finding (ADR 0162, issue #724).
   */
  copy_of?: string | null | undefined;
  body: string | null;
  handled: { reason: string | null; by: string | null; at: string } | null;
}

export interface AiJob {
  id: string;
  name: string;
  /** Signed, or past signing: a go-ahead on it has been acted on. */
  sold: boolean;
}

export interface AiTeamInput {
  terms?: readonly AiTermsRow[];
  materials?: readonly AiMaterialsRow[];
  readings?: readonly AiReadingRow[];
  jobs: readonly AiJob[];
  /** Payments recorded, for "says they paid". */
  payments?: readonly { job_id: string; at: string }[];
}

const DAY = 86_400_000;

const asReason = (value: string | null | undefined): AiReason | null =>
  value === "wrong" || value === "fine" ? value : null;

const clip = (text: string, max = 300) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

/** Whether `at` is within `days` of `now`. */
export function aiWithinDays(
  at: string | null | undefined,
  now: Date,
  days: number,
): boolean {
  if (!at) return false;
  return now.getTime() - new Date(at).getTime() <= days * DAY;
}

/** A finding before its employee has said it. */
type Draft = Omit<AiFinding, "voice" | "reopenable">;

type State = Pick<
  AiFinding,
  "status" | "reason" | "dismissedBy" | "dismissedAt"
>;

const OPEN_STATE: State = {
  status: "open",
  reason: null,
  dismissedBy: null,
  dismissedAt: null,
};

/** Who dismissed a check, why and when. */
interface CheckDismissal {
  dismissed_reason?: string | null | undefined;
  dismissed_by?: string | null | undefined;
  dismissed_at?: string | null | undefined;
}

/**
 * A paperwork check's state on the page: open while it warns; dismissed while
 * the dismissal still matches and is under 90 days old; otherwise not shown.
 */
function checkState(
  warns: boolean,
  dismissed: boolean,
  row: CheckDismissal,
  now: Date,
): State | null {
  if (warns) return OPEN_STATE;
  if (!dismissed || !aiWithinDays(row.dismissed_at, now, AI_DISMISSED_DAYS)) {
    return null;
  }
  return {
    status: "dismissed",
    reason: asReason(row.dismissed_reason),
    dismissedBy: row.dismissed_by ?? null,
    dismissedAt: row.dismissed_at ?? null,
  };
}

/** How sure, the house way: under 90% the page says "Looks like". */
function sureness(value: number | string) {
  const probability = Number(value);
  return { probability, likely: replyCertainty(probability) !== "stated" };
}

function termsFinding(row: AiTermsRow, now: Date): Draft | null {
  const warning = paymentTermsWarning(row);
  const state = checkState(
    warning !== null,
    Number(row.probability) >= TERMS_WARN_AT && termsDismissed(row),
    row,
    now,
  );
  if (!state) return null;
  return {
    key: `terms:${row.job_id}`,
    source: "terms",
    sourceId: row.job_id,
    employee: "paige",
    jobId: row.job_id,
    title: "Promised payment terms the contract doesn’t show",
    said: row.quote
      ? { label: warning?.source ?? "The conversation", text: row.quote }
      : null,
    record: {
      label: "The contract",
      text: "Its payment schedule doesn’t show this.",
    },
    summary: null,
    ...sureness(row.probability),
    raisedAt: row.quote_at ?? row.checked_at,
    card: cardOf(row.quote_kind),
    ...state,
  };
}

function materialsFinding(row: AiMaterialsRow, now: Date): Draft | null {
  const warning = materialsWarning(row);
  const state = checkState(
    warning !== null,
    Number(row.probability) >= MATERIALS_WARN_AT &&
      Boolean(row.line && row.quote) &&
      materialsDismissed(row),
    row,
    now,
  );
  if (!state) return null;
  return {
    key: `materials:${row.job_id}`,
    source: "materials",
    sourceId: row.job_id,
    employee: "max",
    jobId: row.job_id,
    title: "The order doesn’t show what was asked for",
    said: {
      label: warning?.source ?? shortDayMonth(row.quote_at ?? "") ?? "",
      text: materialsQuote(row.quote ?? ""),
    },
    record: {
      label: row.order_draft ? "Draft order" : "Placed order",
      text: row.line ?? "",
    },
    summary: row.summary?.trim() || null,
    ...sureness(row.probability),
    raisedAt: row.quote_at ?? row.checked_at,
    card: cardOf(row.quote_kind),
    ...state,
  };
}

/** A go-ahead or a payment, from a customer, sure enough, on a known job. */
function readable(row: AiReadingRow): boolean {
  return (
    (row.meaning === "accepted" || row.meaning === "paid") &&
    row.sender_kind === "customer" &&
    Number(row.probability) >= REPLY_LIKELY_FROM
  );
}

/** Has what the reply said already happened? */
function settled(
  row: AiReadingRow,
  job: AiJob,
  payments: readonly { job_id: string; at: string }[],
): boolean {
  if (row.meaning === "accepted") return job.sold;
  const sent = new Date(row.sent_at).getTime();
  return payments.some(
    (p) => p.job_id === row.job_id && new Date(p.at).getTime() > sent,
  );
}

function readingState(
  row: AiReadingRow,
  job: AiJob,
  payments: readonly { job_id: string; at: string }[],
  now: Date,
): State | null {
  const handled = row.handled;
  if (!handled) {
    if (!aiWithinDays(row.sent_at, now, AI_READING_DAYS)) return null;
    return settled(row, job, payments) ? null : OPEN_STATE;
  }
  if (!aiWithinDays(handled.at, now, AI_DISMISSED_DAYS)) return null;
  return {
    status: "dismissed",
    reason: asReason(handled.reason),
    dismissedBy: handled.by,
    dismissedAt: handled.at,
  };
}

function readingFinding(
  row: AiReadingRow,
  jobs: ReadonlyMap<string, AiJob>,
  payments: readonly { job_id: string; at: string }[],
  now: Date,
): Draft | null {
  const job = row.job_id ? jobs.get(row.job_id) : undefined;
  if (!job || !readable(row)) return null;
  const state = readingState(row, job, payments, now);
  if (!state) return null;
  const sure = sureness(row.probability);
  const accepted = row.meaning === "accepted";
  const email = row.kind === "email";
  return {
    key: `reading:${row.id}`,
    source: "reading",
    sourceId: row.id,
    employee: "iris",
    jobId: job.id,
    title: `${sure.likely ? "Looks like " : ""}${job.name} ${
      accepted ? "said go ahead" : "says they paid"
    }`,
    said: {
      label: `${email ? "Email" : "Text"}, ${shortDayMonth(row.sent_at) ?? ""}`,
      text: clip((row.body ?? "").trim()),
    },
    record: accepted
      ? { label: "The job", text: "Not signed yet." }
      : { label: "Payments", text: "None recorded since." },
    summary: null,
    ...sure,
    raisedAt: row.sent_at,
    card: email ? "Emails" : "Texts",
    ...state,
  };
}

/** The finding as its employee would say it. */
function voiceOf(f: Draft, name: string): string {
  switch (f.source) {
    case "terms":
      return `${name} was promised a way of paying that the contract doesn’t show.`;
    case "materials":
      return `What ${name} asked for isn’t on the ${f.record?.label.toLowerCase() ?? "order"}.`;
    case "reading": {
      const how = f.card === "Emails" ? "emailed" : "texted";
      return f.title.endsWith("said go ahead")
        ? `${name} ${how} what reads as a go-ahead. The job isn’t signed yet.`
        : `${name} ${how} that they’ve paid. No payment is recorded since.`;
    }
  }
}

/**
 * One reading per email, however many mailboxes it reached: the copy with the
 * lowest id among those on a known job that read as a finding, as the to-do
 * list keeps it, carrying the dismissal if any copy has one. Each mailbox copy
 * is read on its own, so one may have no job or a weaker reading while the
 * other makes the finding (issue #727). A dismissal given here acts on every
 * copy, so they agree; one marked handled on Messages may not, and a reason
 * wins over none.
 */
function oneCopyEach(
  readings: readonly AiReadingRow[],
  jobs: ReadonlyMap<string, AiJob>,
): AiReadingRow[] {
  const groups = new Map<string, AiReadingRow[]>();
  for (const row of readings) {
    const key = row.copy_of?.trim() || `id:${row.id}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return [...groups.values()].map((copies) => {
    const finding = copies.filter(
      (c) => c.job_id !== null && jobs.has(c.job_id) && readable(c),
    );
    const pool = finding.length > 0 ? finding : copies;
    const first = pool.reduce((low, c) => (c.id < low.id ? c : low));
    const handled =
      copies.find((c) => c.handled?.reason)?.handled ??
      copies.find((c) => c.handled)?.handled ??
      null;
    return { ...first, handled };
  });
}

/** Every finding the page shows, newest first. */
export function aiFindings(input: AiTeamInput, now: Date): AiFinding[] {
  const jobs = new Map(input.jobs.map((job) => [job.id, job]));
  const named = (finding: Draft): AiFinding => ({
    ...finding,
    reopenable:
      finding.status === "dismissed" &&
      (finding.source !== "reading" || finding.reason !== null),
    voice: voiceOf(
      finding,
      (finding.jobId && jobs.get(finding.jobId)?.name) || "The customer",
    ),
  });
  const all = [
    ...oneCopyEach(input.readings ?? [], jobs).map((row) =>
      readingFinding(row, jobs, input.payments ?? [], now),
    ),
    ...(input.terms ?? []).map((row) => termsFinding(row, now)),
    ...(input.materials ?? []).map((row) => materialsFinding(row, now)),
  ]
    .filter((finding) => finding !== null)
    .map(named);
  return all.sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));
}
